// src/pages/QRCodeGeneratorPage.js
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { QRCodeCanvas } from 'qrcode.react';
import LoadingSpinner from '../components/loadingspinner';
import { trackPageView, trackAdminAction } from '../services/analyticsService';
import QRCodeDownloadTemplate from '../components/QRCodeDownloadTemplate';
import './QRCodeGeneratorPage.css';

function QRCodeGeneratorPage() {
  const { restaurantId } = useParams();
  const navigate = useNavigate();
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [qrType, setQrType] = useState('menu'); // Default to menu QR code
  const [tableNumber, setTableNumber] = useState('1'); // Default table number
  const [selectedSeatAreaId, setSelectedSeatAreaId] = useState('');
  const [seatAreas, setSeatAreas] = useState([]);
  const [showQRModal, setShowQRModal] = useState(false);
  const [currentQrUrl, setCurrentQrUrl] = useState('');
  const [qrTitle, setQrTitle] = useState('Menu QR Code');

  useEffect(() => {
    trackPageView('QR Code Generator', `/qr-generator/${restaurantId}`, restaurantId);
  }, [restaurantId]);

  useEffect(() => {
    if (!restaurantId) {
      setError('Restaurant ID is missing.');
      setLoading(false);
      return;
    }

    const fetchRestaurantData = async () => {
      try {
        setLoading(true);
        const restaurantRef = doc(db, 'restaurants', restaurantId);
        const restaurantSnap = await getDoc(restaurantRef);

        if (restaurantSnap.exists()) {
          const data = restaurantSnap.data();
          setRestaurant({ id: restaurantSnap.id, ...data });
          
          if (Array.isArray(data.seatCategories)) {
             setSeatAreas(data.seatCategories);
             if (data.seatCategories.length > 0) {
                 setSelectedSeatAreaId(data.seatCategories[0].id);
             }
          }

          trackAdminAction('view_qr_generator', { restaurantId: restaurantSnap.id, restaurantName: data.name });
        } else {
          setError('Restaurant not found.');
          navigate('/404');
        }
      } catch (err) {
        console.error('Error fetching restaurant data:', err);
        setError('Failed to load restaurant data.');
      } finally {
        setLoading(false);
      }
    };

    fetchRestaurantData();
  }, [restaurantId, navigate]);

  // Update QR code URL when type, table number, or seat category changes
  useEffect(() => {
    if (!restaurant) return;

    const baseUrl = `${window.location.origin}/${restaurant.slug}`;
    let url = '';
    let title = '';

    switch (qrType) {
      case 'menu':
        url = baseUrl;
        title = 'Menu QR Code';
        break;
      case 'tablebooking':
        let queryParams = [];
        if (tableNumber) queryParams.push(`table=${tableNumber}`);
        if (selectedSeatAreaId && seatAreas.length > 1) queryParams.push(`area=${selectedSeatAreaId}`);
        
        const queryString = queryParams.length > 0 ? `?${queryParams.join('&')}` : '';

        url = `${baseUrl}/table-booking${queryString}`;
        
        title = tableNumber 
          ? `Table ${tableNumber} Booking QR Code` 
          : 'Table Booking QR Code';
        break;
      case 'onlineorder':
        url = `${baseUrl}/online-order`;
        title = 'Online Order QR Code';
        break;
      default:
        url = baseUrl;
        title = 'Menu QR Code';
    }

    setCurrentQrUrl(url);
    setQrTitle(title);
  }, [qrType, tableNumber, restaurant, selectedSeatAreaId, seatAreas]);

  const handleQrTypeChange = (type) => {
    setQrType(type);
    trackAdminAction('change_qr_type', { restaurantId, type });
  };

  const handleTableNumberChange = (e) => {
    setTableNumber(e.target.value);
  };

  const handleSeatAreaChange = (e) => {
      setSelectedSeatAreaId(e.target.value);
  };

  const handleDownloadClick = () => {
    trackAdminAction('download_qr', { restaurantId, type: qrType, tableNumber: qrType === 'tablebooking' ? tableNumber : null });
    setShowQRModal(true);
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <div className="container error-message">{error}</div>;
  }

  if (!restaurant) {
    return <div className="container">Restaurant data could not be loaded.</div>;
  }

  return (
    <div className="qr-generator-container">
      <header className="qr-generator-header">
        <h1>QR Code Generator - {restaurant.name}</h1>
        <div className="header-buttons">
          <Link to={`/dashboard/${restaurantId}`} className="btn btn-outline">Back to Dashboard</Link>
        </div>
      </header>

      <div className="qr-generator-content">
        <div className="qr-options-panel">
          <h2>QR Code Options</h2>
          
          <div className="qr-type-selector">
            <h3>Select QR Code Type</h3>
            <div className="qr-type-buttons">
              <button 
                className={`qr-type-btn ${qrType === 'menu' ? 'active' : ''}`}
                onClick={() => handleQrTypeChange('menu')}
              >
                Menu QR Code
              </button>
              <button 
                className={`qr-type-btn ${qrType === 'tablebooking' ? 'active' : ''}`}
                onClick={() => handleQrTypeChange('tablebooking')}
              >
                Table Booking QR Code
              </button>
              <button 
                className={`qr-type-btn ${qrType === 'onlineorder' ? 'active' : ''}`}
                onClick={() => handleQrTypeChange('onlineorder')}
              >
                Online Order QR Code
              </button>
            </div>
          </div>

          {qrType === 'tablebooking' && (
            <>
                <div className="table-number-selector">
                <h3>Table Number</h3>
                <input 
                    type="number" 
                    min="1"
                    value={tableNumber}
                    onChange={handleTableNumberChange}
                    className="table-number-input"
                />
                <p className="table-number-help">Enter the table number for this QR code</p>
                </div>

                {seatAreas.length > 1 && (
                    <div className="table-number-selector" style={{marginTop: '20px'}}>
                         <h3>Seating Area</h3>
                         <select
                            value={selectedSeatAreaId}
                            onChange={handleSeatAreaChange}
                            className="table-number-input" // Reusing same class for styling
                            style={{width: '100%', maxWidth: 'none', padding: '10px'}}
                         >
                             {seatAreas.map(cat => (
                                 <option key={cat.id} value={cat.id}>{cat.name}</option>
                             ))}
                         </select>
                         <p className="table-number-help">Select the seating area for this QR code</p>
                    </div>
                )}
            </>
          )}

          <div className="qr-url-display">
            <h3>QR Code URL</h3>
            <div className="url-box">
              <p>{currentQrUrl}</p>
            </div>
          </div>
        </div>

        <div className="qr-preview-panel">
          <h2>{qrTitle}</h2>
          <div className="qr-preview">
            {currentQrUrl && (
              <div className="qr-code-wrapper">
                <QRCodeCanvas value={currentQrUrl} size={200} includeMargin={true} />
              </div>
            )}
          </div>
          <button 
            onClick={handleDownloadClick} 
            className="btn btn-primary download-btn"
            disabled={!currentQrUrl}
          >
            Download QR Code
          </button>
        </div>
      </div>

      {showQRModal && currentQrUrl && (
        <div className="qr-modal-overlay">
          <div className="qr-modal-content">
            <button className="qr-modal-close" onClick={() => setShowQRModal(false)}>×</button>
            <QRCodeDownloadTemplate menuUrl={currentQrUrl} restaurantName={restaurant.name} />
          </div>
        </div>
      )}
    </div>
  );
}

export default QRCodeGeneratorPage;